import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { ApnsConfiguration } from "@/types/apns";
import { ApnsConfigurationCard } from "./ApnsConfigurationCard";
import { ApnsConfigurationDialog } from "./ApnsConfigurationDialog";
import { useToast } from "@/hooks/use-toast";
import { Plus, Shield } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import * as tenantsApi from "@/services/tenantsApi";

export function ApnsConfigurationList() {
  const { toast } = useToast();
  const [configurations, setConfigurations] = useState<ApnsConfiguration[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingConfig, setEditingConfig] = useState<ApnsConfiguration | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [configToDelete, setConfigToDelete] = useState<ApnsConfiguration | null>(null);

  const loadConfigurations = async () => {
    setIsLoading(true);
    try {
      const info = await tenantsApi.getApnsCertInfo();
      // If the API returns a single cert info object, wrap it
      if (info && typeof info === 'object') {
        setConfigurations(info ? [info as any] : []);
      }
    } catch { setConfigurations([]); }
    finally { setIsLoading(false); }
  };

  useEffect(() => { loadConfigurations(); }, []);

  const handleDeleteConfirm = async () => {
    if (!configToDelete) return;
    try {
      await tenantsApi.deleteApnsCert();
      toast({ title: "Success", description: "APNs configuration deleted successfully" });
      loadConfigurations();
    } catch (error: any) {
      toast({ title: "Error", description: error.message || "Failed to delete configuration", variant: "destructive" });
    } finally { setDeleteDialogOpen(false); setConfigToDelete(null); }
  };

  if (isLoading) return <div className="text-center py-8 text-muted-foreground">Loading APNs configurations...</div>;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div><h3 className="text-lg font-semibold">APNs Certificates</h3><p className="text-sm text-muted-foreground">Manage Apple Push Notification service certificates</p></div>
        <Button type="button" onClick={() => { setEditingConfig(null); setDialogOpen(true); }} className="gap-2"><Plus className="h-4 w-4" />Add APNs Configuration</Button>
      </div>
      {configurations.length === 0 ? (
        <div className="border-2 border-dashed rounded-lg p-12 text-center">
          <Shield className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
          <h3 className="text-lg font-semibold">No APNs Certificates Configured</h3>
          <p className="text-sm text-muted-foreground">APNs certificates are required for push notifications to Apple devices.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {configurations.map((config) => <ApnsConfigurationCard key={config.id} config={config} onEdit={() => { setEditingConfig(config); setDialogOpen(true); }} onDelete={() => { setConfigToDelete(config); setDeleteDialogOpen(true); }} />)}
        </div>
      )}
      <ApnsConfigurationDialog open={dialogOpen} onOpenChange={setDialogOpen} onSuccess={loadConfigurations} initialData={editingConfig} />
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Delete APNs Configuration?</AlertDialogTitle><AlertDialogDescription>Are you sure? Devices will no longer receive push notifications.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={handleDeleteConfirm} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Delete</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
